<script setup>
import { computed, nextTick, ref, watch } from "vue";
import ToggleButtons from "./ui/formkit/ToggleButtons.vue";
import { ArrowTopRightOnSquareIcon, TrashIcon } from "@heroicons/vue/24/outline";
import { createInput } from "@formkit/vue";
import { useI18n } from "vue-i18n";
import KnocCardPreview from "./KnocCardPreview.vue";
import globals from "../globals";
import { useUserStore } from "../pinia/user.ts";
import Tooltip from "./ui/Tooltip.vue";

const { t } = useI18n();

const userStore = useUserStore();
userStore.fetchAcls().then(
  () => {},
  () => console.log(globals.renderError(t, error)),
);

const props = defineProps({
  oldRestrictionType: {
    type: String,
    default: "none",
  },
  oldRanges: {
    type: Array,
    default: [{ State: "", End: "" }],
  },
  type: {
    type: String,
  },
  form: {
    required: false,
  },
  aclId: {
    type: String,
  },
});

const autoComplete = import.meta.env?.VITE_AUTOCOMPLETE || "off";

const restrictionTypes = ["none", "RFC1918", "allowList", "denyList"];
const rfcOptions = ["allow", "deny"];

const toggleButtons = createInput(ToggleButtons, {
  props: ["options"],
});

function isValidIpv4Address(ip) {
  const parts = ip.split(".");
  if (parts.length !== 4) return false;

  return parts.every((part) => {
    if (!/^\d+$/.test(part)) return false;
    const num = Number(part);
    return num >= 0 && num <= 255 && !(part.length > 1 && part.startsWith("0"));
  });
}

function validateIp(siblingNodeName) {
  return function (node) {
    const ip = node.value;
    const siblingIp = node.parent.value[siblingNodeName];

    if (!ip && !siblingIp) {
      return true;
    }
    if (!ip || !siblingIp) {
      return false;
    }

    if (!isValidIpv4Address(ip)) {
      return false;
    }
    if (!isValidIpv4Address(siblingIp)) {
      return true;
    }

    return true;
  };
}

const cloneRange = (r) => r.map((v) => ({ Start: v.Start, End: v.End }));

const restrictionType = ref("none");
const rfcType = ref(null);
const activeRanges = ref(null);

const maximumGrantDuration = ref(null);
const clickToGrant = ref(false);
const enterTicketNumber = ref(false);
const requireTOTP = ref(false);
const hideFromUsers = ref(false);
const autoBrowseUsers = ref(false);

const nonRfcRanges = ref(
  props.oldRestrictionType !== "RFC1918" ? cloneRange(props.oldRanges) : [{ State: "", End: "" }],
);

watch(
  restrictionType,
  (newValue, oldValue) => {
    nextTick(() => {
      if (oldValue && oldValue !== "RFC1918") {
        nonRfcRanges.value = cloneRange(activeRanges.value);
      }

      if (newValue === "RFC1918") {
        if (!rfcType.value) {
          rfcType.value = rfcOptions[0];
        }

        activeRanges.value = [
          { Start: "10.0.0.0", End: "10.255.255.255" },
          { Start: "172.16.0.0", End: "172.31.255.255" },
          { Start: "192.168.0.0", End: "192.168.255.255" },
        ];
      } else {
        activeRanges.value = cloneRange(nonRfcRanges.value);
      }
    });
  },
  { immediate: true },
);

const acls = ref([]);

const candidateParentKnocs = computed(() => {
  var candidates = [{ label: "None", value: "" }];

  acls.value.forEach((acl) => {
    if (acl.ClickToGrant && !acl.ParentID && acl.ID != props.aclId) {
      candidates.push({ label: acl.Description, value: acl.ID });
    }
  });

  return candidates;
});

const grantAppliesTo = ["userIp", "specifiedIps"];
const selectedGrantAppliesTo = ref(grantAppliesTo[0]);
const grantAppliesToIps = ref([]);

const knocOptionsForm = defineModel();
</script>

<template>
  <FormKit v-model="knocOptionsForm" type="form" :actions="false">
    <div class="flex flex-col space-y-6">
      <div class="grid grid-cols-2 gap-6">
        <div>
          <div class="space-y-6 rounded-lg border p-6 shadow-sm">
            <div class="flex items-start justify-between">
              <div class="space-y-1.5">
                <h3 class="scroll-m-20 text-2xl font-semibold tracking-tight">
                  {{ t("pages.wizard.steps.settings.fields.Options.grantOptionsHeading") }}
                </h3>
              </div>

              <a
                class="inline-flex items-center gap-1 rounded-md border border-transparent bg-secondary px-2.5 py-0.5 text-xs font-semibold text-secondary-foreground transition-colors hover:bg-secondary/80 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                href="https://docs.knocknoc.io/books/admin-guide/chapter/access"
                target="_blank"
                aria-label="Access grants documentation (opens in new tab)"
              >
                {{ t("common.docs") }}
                <ArrowTopRightOnSquareIcon class="h-3 w-3" />
                <span class="sr-only">{{ t("common.opensInNewTab") }}</span>
              </a>
            </div>

            <div class="flex flex-col">
              <FormKit
                v-model="clickToGrant"
                name="ClickToGrant"
                :label="t('pages.wizard.steps.settings.fields.ClickToGrant.label')"
                type="checkbox"
              />
              <Tooltip
                :text="!clickToGrant ? '\'Click to grant\' is required for this feature' : ''"
                :follow-cursor="true"
              >
                <FormKit
                  :disabled="!clickToGrant"
                  v-model="enterTicketNumber"
                  name="EnterTicketNumber"
                  :label="t('pages.wizard.steps.settings.fields.EnterTicketNumber.label')"
                  type="checkbox"
                />
              </Tooltip>

              <Tooltip
                :text="!clickToGrant ? '\'Click to grant\' is required for this feature' : ''"
                :follow-cursor="true"
              >
                <FormKit
                  :disabled="!clickToGrant"
                  v-model="requireTOTP"
                  name="RequireTOTP"
                  :label="t('pages.wizard.steps.settings.fields.RequireTOTP.label')"
                  type="checkbox"
                />
              </Tooltip>

              <FormKit
                v-model="maximumGrantDuration"
                :classes="{
                  outer: 'max-w-full',
                }"
                name="MaximumGrantDuration"
                :label="t('pages.wizard.steps.settings.fields.MaximumGrantDuration.label')"
                type="number"
                number="integer"
                validation="min:5|max:40320"
                :autocomplete="autoComplete"
              />
            </div>

            <FormKit type="group" name="IpExclusions">
              <div class="space-y-4 shadow-sm">
                <div class="flex items-start justify-between">
                  <h3 class="scroll-m-20 text-2xl font-semibold tracking-tight">
                    {{ t("pages.wizard.steps.settings.fields.IpRestrictions.heading") }}
                  </h3>

                  <a
                    class="inline-flex items-center gap-1 rounded-md border border-transparent bg-secondary px-2.5 py-0.5 text-xs font-semibold text-secondary-foreground transition-colors hover:bg-secondary/80 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    href="https://docs.knocknoc.io/books/admin-guide/page/vpns-internal-addresses-and-access"
                    target="_blank"
                    aria-label="VPN and internal addressess documentation (opens in new tab)"
                  >
                    {{ t("common.docs") }}
                    <ArrowTopRightOnSquareIcon class="h-3 w-3" />
                    <span class="sr-only">{{ t("common.opensInNewTab") }}</span>
                  </a>
                </div>

                <p class="text-sm text-foreground">
                  {{ t("pages.wizard.steps.settings.fields.IpRestrictions.description") }}
                </p>

                <div class="w-full">
                  <FormKit
                    v-model="restrictionType"
                    :classes="{
                      outer: 'w-full',
                    }"
                    name="RestrictionType"
                    :options="
                      restrictionTypes.map((option) => ({
                        label: t(`pages.wizard.steps.settings.fields.IpRestrictions.options.${option}`),
                        value: option,
                      }))
                    "
                    :type="toggleButtons"
                  />
                </div>
              </div>

              <div v-if="restrictionType === 'RFC1918'" class="!mt-0">
                <FormKit
                  v-model="rfcType"
                  :classes="{
                    outer: 'w-full',
                  }"
                  name="RfcType"
                  :options="
                    rfcOptions.map((option) => ({
                      label: t(`pages.wizard.steps.settings.fields.IpRestrictions.rfc.options.${option}`),
                      value: option,
                    }))
                  "
                  :type="toggleButtons"
                />
                <p class="text-sm text-muted-foreground">
                  {{ t("pages.wizard.steps.settings.fields.IpRestrictions.rfc.description") }}
                </p>
              </div>

              <FormKit
                v-if="restrictionType !== 'none'"
                v-model="activeRanges"
                type="list"
                name="Ranges"
                dynamic
                #default="{ items, node, value }"
              >
                <div class="flex flex-col">
                  <FormKit v-for="(item, index) in items" type="group" :key="index" :index="index">
                    <div class="flex space-x-4">
                      <FormKit
                        type="text"
                        name="Start"
                        :label="t(`pages.wizard.steps.settings.fields.IpRestrictions.fields.start.label`)"
                        :placeholder="t('pages.wizard.steps.settings.fields.IpRestrictions.fields.start.placeholder')"
                        :disabled="restrictionType === 'RFC1918'"
                        :validation-rules="{
                          validateStart: validateIp('End'),
                        }"
                        validation="validateStart"
                        :autocomplete="autoComplete"
                      />

                      <FormKit
                        type="text"
                        name="End"
                        :label="t(`pages.wizard.steps.settings.fields.IpRestrictions.fields.end.label`)"
                        :placeholder="t('pages.wizard.steps.settings.fields.IpRestrictions.fields.end.placeholder')"
                        :disabled="restrictionType === 'RFC1918'"
                        :validation-rules="{
                          validateEnd: validateIp('Start'),
                        }"
                        validation="validateEnd"
                        :autocomplete="autoComplete"
                      />

                      <button
                        v-if="restrictionType !== 'RFC1918'"
                        class="mt-[26px] inline-flex h-10 w-10 items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                        type="button"
                        @click="() => node.input(value.filter((_, i) => i !== index))"
                        aria-label="Delete IP range"
                      >
                        <TrashIcon class="h-4 w-4 cursor-pointer" />
                      </button>
                    </div>
                  </FormKit>

                  <button
                    v-if="restrictionType !== 'RFC1918'"
                    data-testid="add-ip-restriction-button"
                    type="button"
                    @click="() => node.input(value.concat({}))"
                    class="mb-4 mt-2 inline-flex h-9 w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-input bg-background px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                  >
                    {{ t("pages.wizard.steps.settings.fields.IpRestrictions.add") }}
                  </button>
                </div>
              </FormKit>
            </FormKit>

            <div class="space-y-4">
              <h3 class="scroll-m-20 text-2xl font-semibold tracking-tight">
                {{ t("pages.wizard.steps.settings.fields.GrantType.heading") }}
              </h3>
              <p class="text-sm text-foreground">
                {{ t("pages.wizard.steps.settings.fields.GrantType.description") }}
              </p>
              <div class="flex flex-col">
                <FormKit type="group" name="GrantType">
                  <FormKit
                    v-model="selectedGrantAppliesTo"
                    :classes="{
                      outer: 'w-full',
                    }"
                    name="Type"
                    :options="
                      grantAppliesTo.map((option) => ({
                        label: t(`pages.wizard.steps.settings.fields.GrantAppliesTo.options.${option}`),
                        value: option,
                      }))
                    "
                    :type="toggleButtons"
                  />

                  <FormKit
                    v-if="selectedGrantAppliesTo !== 'userIp'"
                    v-model="grantAppliesToIps"
                    type="list"
                    name="Ips"
                    dynamic
                    #default="{ items, node, value }"
                  >
                    <div class="flex flex-col">
                      <FormKit v-for="(item, index) in items" type="group" :key="index" :index="index">
                        <div class="flex space-x-4">
                          <FormKit
                            type="text"
                            name="Ip"
                            :label="t(`IP address`)"
                            :placeholder="t('10.0.0.255')"
                            :validation-rules="{
                              validateIpAddress: (n) => isValidIpv4Address(n.value),
                            }"
                            validation="required|validateIpAddress"
                            :validation-messages="{ validateIpAddress: 'Invalid IP address' }"
                            :autocomplete="autoComplete"
                          />
                          <button
                            class="mt-[26px] inline-flex h-10 w-10 items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                            type="button"
                            @click="() => node.input(value.filter((_, i) => i !== index))"
                          >
                            <TrashIcon class="h-4 w-4 cursor-pointer" />
                          </button>
                        </div>
                      </FormKit>

                      <button
                        data-testid="add-grant-ip-button"
                        type="button"
                        @click="() => node.input(value.concat({}))"
                        class="mb-4 mt-2 inline-flex h-9 w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-input bg-background px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                      >
                        {{ t("Add IP address") }}
                      </button>
                    </div>
                  </FormKit>
                </FormKit>
              </div>
            </div>

            <h3 class="scroll-m-20 text-2xl font-semibold tracking-tight">
              {{ t("pages.wizard.steps.settings.fields.Options.additionalOptionsHeading") }}
            </h3>
            <div class="flex flex-col">
              <!--
              <FormKit
                v-if="discoverEnabled"
                name="CGNATOpen"
                :label="t('pages.wizard.steps.settings.fields.CGNATOpen.label')"
                type="checkbox"
              />
			  -->

              <FormKit
                v-model="hideFromUsers"
                name="HideFromUsers"
                :label="t('pages.wizard.steps.settings.fields.HideFromUsers.label')"
                type="checkbox"
              />

              <FormKit
                v-model="autoBrowseUsers"
                name="AutoBrowseUsers"
                :label="t('pages.wizard.steps.settings.fields.AutoBrowseUsers.label')"
                type="checkbox"
              />
            </div>
          </div>
        </div>

        <div>
          <KnocCardPreview :form="form" :knoc-options-form="knocOptionsForm" />
        </div>
      </div>

      <div class="flex justify-end space-x-4">
        <slot />
      </div>
    </div>
  </FormKit>
</template>
